import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Lock, Crown, Zap, ArrowRight } from 'lucide-react';

/**
 * Wraps premium content. Shows a teaser + CTA if the user is not authenticated
 * or has no subscription.
 *
 * MOAT UPSELL: for authenticated-but-unsubscribed users we replace the generic
 * "Start Free Trial" copy with a pitch built from their OWN data — decisions
 * logged + how much their bias-free Digital Twin is beating them by. The moat
 * data becomes the conversion pitch ("Your Twin is up $2,130 more than you —
 * unlock Stock Analysis to close the gap."). Falls back to generic copy when
 * there's no data yet or the fetch fails.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.gammaqc.com';
const TOKEN_KEY = 'fio_session';
const fmtUSD = (n) => {
  if (n == null) return null;
  const s = n < 0 ? '-' : '';
  return `${s}$${Math.abs(Math.round(n)).toLocaleString()}`;
};

// Metered-free: an unauthenticated (cold) visitor gets this many real analyses
// before the signup wall. This is the Wave-1 ad funnel — the ad promises a free
// read, so /analysis must deliver one without a login in front of it. Bump this
// if you want cold visitors to try more tickers before the wall.
const FREE_ANALYSIS_LIMIT = 1;

export function SubscriptionGate({ feature, teaser, metered, children }) {
  const { user, isAuthenticated, hasActiveSubscription } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [moat, setMoat] = useState(null); // { decisions, deltaPnl, twinReturn }

  const subscribed = isAuthenticated && hasActiveSubscription();

  // How many free analyses this cold visitor has already run (set by
  // EnhancedAnalysis on each completed anon analysis).
  let freeUsed = 0;
  try { freeUsed = Number(localStorage.getItem('gqc_free_analyses') || 0); } catch (_) { /* no storage */ }
  const meteredAllowed = metered && !isAuthenticated && freeUsed < FREE_ANALYSIS_LIMIT;

  // Pull the user's own moat numbers to personalize the pitch (auth'd + unsub only).
  useEffect(() => {
    if (!isAuthenticated || subscribed) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    let cancelled = false;
    Promise.allSettled([
      fetch(`${BACKEND_URL}/api/me/twin`, { headers: h }).then((r) => (r.ok ? r.json() : null)),
      fetch(`${BACKEND_URL}/api/me/scorecard`, { headers: h }).then((r) => (r.ok ? r.json() : null)),
    ]).then(([twinR, scoreR]) => {
      if (cancelled) return;
      const twin = twinR.status === 'fulfilled' ? twinR.value : null;
      const score = scoreR.status === 'fulfilled' ? scoreR.value : null;
      const decisions = score?.total_decisions ?? null;
      const deltaPnl = twin?.has_data ? twin.delta_pnl : null;
      const twinReturn = twin?.has_data ? twin?.twin?.return_pct : null;
      if (decisions || deltaPnl != null) setMoat({ decisions, deltaPnl, twinReturn });
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, subscribed]);

  if (subscribed || meteredAllowed) return children;

  // Build the personalized headline + subline.
  let headline = isAuthenticated
    ? `Unlock ${feature || 'this feature'}`
    : `Sign up to access ${feature || 'this feature'}`;
  let subline = isAuthenticated
    ? 'Start your 7-day free trial to unlock all premium analysis. No charge until day 8.'
    : 'Create a free account and start your 7-day trial. Full access to AI-powered trading tools.';

  if (moat) {
    const gap = fmtUSD(moat.deltaPnl);
    if (moat.deltaPnl != null && moat.deltaPnl > 0 && gap) {
      headline = `Your Twin is beating you by ${gap}`;
      subline = `You've logged ${moat.decisions ?? 'several'} decisions. Your bias-free Digital Twin is ${gap} ahead of your actual calls. Unlock ${feature || 'advanced analysis'} to close the gap.`;
    } else if (moat.decisions) {
      headline = `You've logged ${moat.decisions} decisions`;
      subline = `Your track record is building. Unlock ${feature || 'advanced analysis'} to turn it into an edge — deeper Signal Fusion, AI analysis, and more.`;
    }
  }

  // Cold visitor who has spent their free analysis. Two fixes over the old
  // copy: (1) lead with the next ticker's exit level, not "that's over" — a
  // termination-framed headline is the wrong note right after someone got
  // real value; (2) the old subline said signing up would "keep analyzing
  // any ticker," which isn't true — signup alone doesn't unlock more free
  // analyses, it just leads to the trial wall below. Say the real sequence
  // (sign up, THEN the 7-day trial) instead of implying signup itself is
  // the unlock.
  const meteredExhausted = metered && !isAuthenticated && freeUsed >= FREE_ANALYSIS_LIMIT;
  if (meteredExhausted) {
    let lastSymbol = '';
    try { lastSymbol = localStorage.getItem('gqc_last_symbol') || ''; } catch (_) { /* no storage */ }
    headline = lastSymbol
      ? `You just saw ${lastSymbol}'s exit level. Every ticker gets one.`
      : 'Every ticker gets a real exit level.';
    subline = 'Sign up free, then start a 7-day trial — no charge until day 8 — and get the call and the exact price where it\'s wrong on any ticker.';
  }

  return (
    <div className="relative">
      {teaser && (
        <div className="pointer-events-none select-none" style={{ filter: 'blur(3px)', opacity: 0.4 }}>
          {teaser}
        </div>
      )}

      <div className={`${teaser ? 'absolute inset-0' : ''} flex items-center justify-center p-8`}
        style={{ background: teaser ? `${theme.background}dd` : theme.background, backdropFilter: teaser ? 'blur(4px)' : 'none', minHeight: teaser ? 'auto' : '70vh' }}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${theme.primary}30, ${theme.accent}30)` }}>
            {isAuthenticated ? <Crown className="w-8 h-8" style={{ color: theme.primary }} /> : <Lock className="w-8 h-8" style={{ color: theme.primary }} />}
          </div>

          <h3 className="text-xl font-bold mb-2" style={{ color: theme.textPrimary, fontFamily: 'Space Grotesk, sans-serif' }}>
            {headline}
          </h3>

          <p className="text-sm mb-5" style={{ color: theme.textSecondary }}>
            {subline}
          </p>

          <div className="space-y-2">
            <Button data-testid="gate-cta-btn"
              onClick={() => navigate(isAuthenticated ? '/subscribe' : '/register')}
              className="w-full py-3 font-semibold rounded-xl"
              style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`, color: '#0A0A0B' }}>
              <Zap className="w-4 h-4 mr-2" />
              {isAuthenticated ? 'Start Free Trial' : 'Sign Up Free'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            {/* When personalized, offer a peek at the moat data they already own */}
            {moat && isAuthenticated && (
              <button onClick={() => navigate('/me/twin')}
                className="text-sm" style={{ color: theme.textSecondary }}>
                See your Twin <span style={{ color: theme.primary }}>→</span>
              </button>
            )}

            {!isAuthenticated && (
              <button onClick={() => navigate('/login')}
                className="text-sm" style={{ color: theme.textSecondary }}>
                Already have an account? <span style={{ color: theme.primary }}>Sign in</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
